import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Send,
    Download,
    Search,
    RefreshCw,
    FileText,
    Building2,
    Clock,
    User,
    AlertCircle,
    Loader2,
    ExternalLink,
    ArrowLeft,
    Mail,
    Archive,
    Filter,
    Users,
    ChevronDown,
    ChevronUp,
    Euro,
    X
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { useNavigate, Link } from 'react-router-dom';
import { epArchivesAPI } from '@/lib/api';

// Composant pour afficher un dossier archivé
function ArchiveCard({ archive, onDownload, onSendEmail }) {
    const [expanded, setExpanded] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            await onDownload(archive);
        } finally {
            setDownloading(false);
        }
    };

    // Formater les montants en euros
    const formatEuro = (val) => {
        if (val === null || val === undefined) return '-';
        try {
            return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val);
        } catch {
            return `${val} €`;
        }
    };

    return (
        <Card className="border-l-4 border-l-green-500 hover:shadow-md transition-shadow" data-testid={`archive-${archive.id}`}>
            <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center">
                            <Archive className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                                {archive.company_name || 'Entreprise'}
                            </CardTitle>
                            <p className="text-sm text-slate-500 flex items-center gap-2">
                                <User className="w-3 h-3" />
                                {archive.prospect_name || 'Contact'}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link 
                            to={`/prospects/${archive.prospect_id}`}
                            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                            <ExternalLink className="w-3 h-3" />
                            Voir prospect
                        </Link>
                    </div>
                </div>
            </CardHeader>
            
            <CardContent className="space-y-4">
                {/* Infos principales */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                        <span className="text-slate-500">Téléphone</span>
                        <p className="font-medium">{archive.prospect_phone || '-'}</p>
                    </div>
                    <div>
                        <span className="text-slate-500">Email</span>
                        <p className="font-medium truncate">{archive.prospect_email || '-'}</p>
                    </div>
                    <div>
                        <span className="text-slate-500">SIREN</span>
                        <p className="font-medium">{archive.siren || '-'}</p>
                    </div>
                    <div>
                        <span className="text-slate-500">PDL</span>
                        <p className="font-medium">{archive.pdl || '-'}</p>
                    </div>
                </div>

                {/* Référents */}
                <div className="flex flex-wrap gap-2">
                    {archive.team_name && (
                        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                            <Users className="w-3 h-3 mr-1" />
                            {archive.team_name}
                        </Badge>
                    )}
                    {archive.dossier_vp_name && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            VP: {archive.dossier_vp_name}
                        </Badge>
                    )}
                    {archive.dossier_apporteur_name && (
                        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                            App: {archive.dossier_apporteur_name}
                        </Badge>
                    )}
                </div>

                {/* Bouton Détails */}
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpanded(!expanded)}
                    className="w-full justify-between text-slate-600"
                >
                    {expanded ? 'Masquer les détails' : 'Voir les détails (commissions, étude...)'}
                    {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>

                {/* Section détails expandable */}
                {expanded && (
                    <div className="space-y-4 pt-4 border-t animate-in slide-in-from-top-2">
                        {/* Commissions */}
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4">
                            <h4 className="font-semibold text-green-800 flex items-center gap-2 mb-3">
                                <Euro className="w-4 h-4" />
                                Commissions
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                                <div className="bg-white rounded-lg p-3 shadow-sm">
                                    <span className="text-slate-500 text-xs">Commission Totale HT</span>
                                    <p className="font-bold text-lg text-green-700">{formatEuro(archive.commission_ht_ep)}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3 shadow-sm">
                                    <span className="text-slate-500 text-xs">Valnexo</span>
                                    <p className="font-semibold">{formatEuro(archive.ep_commission_valnexo)}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3 shadow-sm">
                                    <span className="text-slate-500 text-xs">AT Service</span>
                                    <p className="font-semibold">{formatEuro(archive.ep_commission_at_service)}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3 shadow-sm">
                                    <span className="text-slate-500 text-xs">VP Payeur ({archive.vp_commission_percent || 0}%)</span>
                                    <p className="font-semibold text-blue-600">{formatEuro(archive.ep_commission_vp_payeur)}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3 shadow-sm">
                                    <span className="text-slate-500 text-xs">VP Superviseur ({archive.vp_2_commission_percent || 0}%)</span>
                                    <p className="font-semibold text-blue-600">{formatEuro(archive.ep_commission_vp_superviseur)}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3 shadow-sm">
                                    <span className="text-slate-500 text-xs">Apporteur ({archive.apporteur_commission_percent || 0}%)</span>
                                    <p className="font-semibold text-orange-600">{formatEuro(archive.ep_commission_apporteur)}</p>
                                </div>
                            </div>
                        </div>

                        {/* Étude */}
                        {archive.etude_text && (
                            <div className="bg-amber-50 rounded-lg p-4">
                                <h4 className="font-semibold text-amber-800 flex items-center gap-2 mb-2">
                                    <FileText className="w-4 h-4" />
                                    Analyse & Étude
                                </h4>
                                <p className="text-sm text-slate-700 whitespace-pre-wrap">{archive.etude_text}</p>
                            </div>
                        )}

                        {/* Fichiers joints */}
                        <div className="bg-slate-50 rounded-lg p-4">
                            <h4 className="font-semibold text-slate-700 mb-2">Fichiers joints</h4>
                            <div className="flex flex-wrap gap-2">
                                {archive.rib_filename && (
                                    <Badge variant="secondary" className="bg-green-100 text-green-700">
                                        RIB: {archive.rib_filename}
                                    </Badge>
                                )}
                                {(archive.attachments || []).map((att, idx) => (
                                    <Badge key={idx} variant="secondary">
                                        {att.filename}
                                    </Badge>
                                ))}
                                {!archive.rib_filename && !(archive.attachments?.length) && (
                                    <span className="text-sm text-slate-500">Aucun fichier</span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Actions et Métadonnées */}
                <div className="flex items-center justify-between pt-3 border-t">
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {archive.archived_at ? format(new Date(archive.archived_at), "dd MMM yyyy à HH:mm", { locale: fr }) : '-'}
                        </span>
                        <span>par {archive.archived_by_name || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button 
                            size="sm" 
                            variant="outline"
                            onClick={handleDownload}
                            disabled={downloading}
                            data-testid={`download-${archive.id}`}
                        >
                            {downloading ? (
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                                <Download className="w-4 h-4 mr-1" />
                            )}
                            Télécharger ZIP
                        </Button>
                        <Button 
                            size="sm"
                            onClick={() => onSendEmail(archive)}
                            data-testid={`send-email-${archive.id}`}
                        >
                            <Mail className="w-4 h-4 mr-1" />
                            Envoyer par email
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

// Page principale des Archives EP
export default function CommunicationPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    
    // États
    const [archives, setArchives] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [total, setTotal] = useState(0);
    const [filterOptions, setFilterOptions] = useState({ teams: [], vps: [] });
    
    // Filtres
    const [showFilters, setShowFilters] = useState(false);
    const [teamFilter, setTeamFilter] = useState('');
    const [vpFilter, setVpFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    
    // Modal email
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [selectedArchive, setSelectedArchive] = useState(null);
    const [emailAddress, setEmailAddress] = useState('');
    const [emailMessage, setEmailMessage] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);

    // Seul admin_full peut accéder aux archives
    const canAccess = (user?.role === 'admin_full' || user?.is_admin === true);

    const fetchArchives = async () => {
        setLoading(true);
        try {
            const params = {
                limit: 100,
                search: searchQuery || undefined,
                team_id: teamFilter || undefined,
                vp_id: vpFilter || undefined,
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined
            };
            
            // Nettoyer les params undefined
            Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
            
            const response = await epArchivesAPI.getAll(params);
            setArchives(response.data.archives || []);
            setTotal(response.data.total || 0);
            setFilterOptions(response.data.filter_options || { teams: [], vps: [] });
        } catch (err) {
            console.error('Fetch error:', err);
            toast.error('Erreur lors du chargement des archives');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (canAccess) {
            fetchArchives();
        }
    }, [canAccess, searchQuery, teamFilter, vpFilter, dateFrom, dateTo]);

    const handleDownload = async (archive) => {
        try {
            const response = await epArchivesAPI.download(archive.id);
            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            const companyName = (archive.company_name || 'Dossier').replace(/[/\\]/g, '-').substring(0, 50);
            link.download = `Dossier_EP_${companyName}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Téléchargement démarré');
        } catch (err) {
            console.error('Download error:', err);
            toast.error('Erreur lors du téléchargement');
        }
    };

    const handleOpenEmailModal = (archive) => {
        setSelectedArchive(archive);
        setEmailAddress('');
        setEmailMessage('');
        setEmailModalOpen(true);
    };

    const handleSendEmail = async () => {
        if (!emailAddress || !emailAddress.includes('@')) {
            toast.error('Veuillez saisir une adresse email valide');
            return;
        }
        
        setSendingEmail(true);
        try {
            await epArchivesAPI.sendEmail(selectedArchive.id, {
                email: emailAddress,
                message: emailMessage
            });
            toast.success(`Dossier envoyé à ${emailAddress}`);
            setEmailModalOpen(false);
        } catch (err) {
            console.error('Send email error:', err);
            toast.error('Erreur lors de l\'envoi de l\'email');
        } finally {
            setSendingEmail(false);
        }
    };

    const clearFilters = () => {
        setTeamFilter('');
        setVpFilter('');
        setDateFrom('');
        setDateTo('');
    };

    const hasActiveFilters = teamFilter || vpFilter || dateFrom || dateTo;

    if (!canAccess) {
        return (
            <div className="p-6">
                <Card className="border-red-200 bg-red-50">
                    <CardContent className="flex items-center gap-4 py-8">
                        <AlertCircle className="w-10 h-10 text-red-500" />
                        <div>
                            <h3 className="font-semibold text-red-800">Accès non autorisé</h3>
                            <p className="text-red-600">Cette section est réservée aux administrateurs et VP.</p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6" data-testid="archives-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate(-1)}
                        className="text-slate-600 hover:text-slate-900"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-5 h-5 mr-1" />
                        Retour
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                            <Archive className="w-7 h-7 text-green-500" />
                            Archives Courtage - Dossiers Complets
                        </h1>
                        <p className="text-slate-500 mt-1">
                            Dossiers Courtage transmis avec RIB, étude et commissions
                        </p>
                    </div>
                </div>
                <Button 
                    variant="outline" 
                    onClick={fetchArchives}
                    disabled={loading}
                    data-testid="refresh-archives"
                >
                    <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
                    Actualiser
                </Button>
            </div>

            {/* Search and filters */}
            <div className="space-y-4">
                <div className="flex items-center gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            placeholder="Rechercher par société, contact, VP, SIREN..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                            data-testid="search-archives"
                        />
                    </div>
                    <Button
                        variant={showFilters ? "secondary" : "outline"}
                        onClick={() => setShowFilters(!showFilters)}
                        className="relative"
                    >
                        <Filter className="w-4 h-4 mr-2" />
                        Filtres
                        {hasActiveFilters && (
                            <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full" />
                        )}
                    </Button>
                    <Badge variant="outline" className="text-slate-600">
                        {total} dossier(s) archivé(s)
                    </Badge>
                </div>

                {/* Filtres avancés */}
                {showFilters && (
                    <Card className="bg-slate-50 animate-in slide-in-from-top-2">
                        <CardContent className="pt-4">
                            <div className="flex items-end gap-4 flex-wrap">
                                {/* Équipe */}
                                <div className="w-48">
                                    <Label className="text-xs text-slate-500">Équipe</Label>
                                    <Select value={teamFilter || "all"} onValueChange={(v) => setTeamFilter(v === "all" ? "" : v)}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Toutes" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Toutes les équipes</SelectItem>
                                            {filterOptions.teams.map(t => (
                                                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                
                                {/* VP */}
                                <div className="w-48">
                                    <Label className="text-xs text-slate-500">VP</Label>
                                    <Select value={vpFilter || "all"} onValueChange={(v) => setVpFilter(v === "all" ? "" : v)}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Tous" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Tous les VP</SelectItem>
                                            {filterOptions.vps.map(v => (
                                                <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                
                                {/* Date de */}
                                <div className="w-40">
                                    <Label className="text-xs text-slate-500">Du</Label>
                                    <Input
                                        type="date"
                                        value={dateFrom}
                                        onChange={(e) => setDateFrom(e.target.value)}
                                    />
                                </div>
                                
                                {/* Date à */}
                                <div className="w-40">
                                    <Label className="text-xs text-slate-500">Au</Label>
                                    <Input
                                        type="date"
                                        value={dateTo}
                                        onChange={(e) => setDateTo(e.target.value)}
                                    />
                                </div>
                                
                                {/* Clear filters */}
                                {hasActiveFilters && (
                                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                                        <X className="w-4 h-4 mr-1" />
                                        Effacer
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Archives list */}
            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
            ) : archives.length === 0 ? (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                        <Archive className="w-12 h-12 text-slate-300 mb-4" />
                        <h3 className="font-semibold text-slate-700">
                            {searchQuery || hasActiveFilters ? 'Aucun résultat' : 'Aucun dossier archivé'}
                        </h3>
                        <p className="text-slate-500 text-sm mt-1">
                            {searchQuery || hasActiveFilters
                                ? 'Modifiez vos critères de recherche.'
                                : 'Les dossiers Courtage complets apparaîtront ici après transmission.'}
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {archives.map((archive) => (
                        <ArchiveCard 
                            key={archive.id} 
                            archive={archive}
                            onDownload={handleDownload}
                            onSendEmail={handleOpenEmailModal}
                        />
                    ))}
                </div>
            )}

            {/* Modal envoi email */}
            <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Mail className="w-5 h-5 text-blue-500" />
                            Envoyer le dossier par email
                        </DialogTitle>
                        <DialogDescription>
                            Le dossier complet sera envoyé avec le PDF récapitulatif, le RIB et les factures.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {selectedArchive && (
                        <div className="space-y-4">
                            <div className="bg-slate-50 rounded-lg p-3">
                                <p className="font-medium">{selectedArchive.company_name}</p>
                                <p className="text-sm text-slate-500">{selectedArchive.prospect_name}</p>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="email">Adresse email du destinataire *</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="destinataire@exemple.com"
                                    value={emailAddress}
                                    onChange={(e) => setEmailAddress(e.target.value)}
                                    data-testid="email-input"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="message">Message personnalisé (optionnel)</Label>
                                <Textarea
                                    id="message"
                                    placeholder="Ajoutez un message à l'email..."
                                    value={emailMessage}
                                    onChange={(e) => setEmailMessage(e.target.value)}
                                    rows={3}
                                />
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEmailModalOpen(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleSendEmail} disabled={sendingEmail || !emailAddress}>
                            {sendingEmail ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Send className="w-4 h-4 mr-2" />
                            )}
                            Envoyer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
